import { EditIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  BoxProps,
  Button,
  Center,
  CloseButton,
  Divider,
  Flex,
  Spinner,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { SolidActionButton } from '@components/Buttons'
import { useApp, useAuth, useCachedUser } from '@hb/react'
import {
  ClinicalRationaleMatch,
  DEFAULT_ENCOUNTER_INPUT_SIZE,
  Encounter,
  encounterFields,
  ENCOUNTERS,
  EncounterSection,
  EncounterSections,
  EncounterSectionType,
  EncounterType,
  EncounterTypeConfig,
  encounterTypeName,
  FieldEncounterSection,
  FieldMapValue,
  FormValidate,
  getDateString,
  getEncounterValidationField,
  getExpectedInitials,
  getFullName,
  greens,
  isEncounterSectionComplete,
  makeAllFieldsOptional,
  prenatalExamField,
  SaveEncounterArgs,
  SectionGenerator,
  TelephoneContact,
  TypedEncounterSection,
  UploadProgress,
  WithId,
} from '@hb/shared'
import { AnyObject, FORM_ERROR, FormApi, ValidationErrors } from 'final-form'
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-final-form'
import {
  autofillEncounterFromNotes,
  getPregnancyClinicalRationale,
  saveEncounter,
  useDocument,
} from '../../../backend'
import { useFormWidth } from '../../../components/forms/FinalForm/context'
import { InnerForm } from '../../../components/forms/FinalForm/InnerForm'
import { FormElement } from '../../../components/forms/Input'
import { useGynCareView } from '../../../components/Gyn/context'
import { DotPhraseProvider } from '../../../contexts/dotPhrase'
import { ThemeProvider } from '../../../contexts/ThemeProvider'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { useAssessmentContacts } from '../../../hooks/backend'
import { getRowBackground, getRowDarkBackground } from '../utils'
import { AutofillContextValue, AutofillProvider } from './AutofillContext'
import { AssessmentPlanInput } from './Data/AssessmentPlanInput'
import { BirthingCenterAdmissionInput } from './Data/BirthingCenterAdmissionInput'
import { ClinicalRationaleInput } from './Data/ClinicalRationaleInput'
import { DepressionScreeningInput } from './Data/DepressionScreeningInput'
import { DvIpvScreeningInput } from './Data/DvIpvScreeningInput'
import { EncounterNotesInput } from './Data/EncounterNotesInput'
import { EncounterVitalsInput } from './Data/EncounterVitalsInput'
import { ExamAllergiesInput } from './Data/ExamAllergiesInput'
import { ExamMedicationsInput } from './Data/ExamMedicationsInput'
import { FirstObVisitInput } from './Data/FirstObVisitInput'
import { GeneralIllnessExamInput } from './Data/GeneralIllnessExamInput'
import { ICD10SelectionInput } from './Data/ICD10SelectionInput'
import { LabsInput } from './Data/LabsInput'
import { MdmInput } from './Data/MdmInput'
import { PatientEducationChecklistInput } from './Data/PatientEducationChecklistInput'
import { PelvicExamInput, PhysicalExamInput } from './Data/PhysicalExamInput'
import { PreVisitContactInput } from './Data/PreVisitContactInput'
import { ReasonForVisitRow } from './Data/ReasonForVisit'
import { RemoteAssessmentInput } from './Data/RemoteAssessmentInput'
import { ReviewOfSystemsInput } from './Data/ReviewOfSystemsInput'
import { RiskAssessmentInput } from './Data/RiskAssessmentInput'
import { SoapNoteInput } from './Data/SoapNoteInput'
import { SubstanceUseCounselingInput } from './Data/SubstanceUseCounselingInput'
import { UltrasoundFindingsInput } from './Data/UltrasoundFindingsInput'
import { UltrasoundHeaderInput } from './Data/UltrasoundHeaderInput'
import { VisitLocationInput } from './Data/VisitLocationInput'
import { EncounterErrorsProvider, useEncounterErrors } from './EncounterErrorsProvider'
import { EncounterFormFooter } from './EncounterFormFooter'
import { EncounterFormProvider, useEncounterForm } from './EncounterFormProvider'
import { EncounterFormHeader, EncounterFormHeaderHandle } from './Header/EncounterFormHeader'
import { useChartingFormSettings } from './hooks/useChartingFormSettings'
import { useEncounterValidate } from './hooks/useEncounterValidate'
import { EncounterSectionNav, getSectionName } from './SectionNav'
import { TimerFlushProvider } from './TimerFlushContext'
import { TimerStateProvider, useTimerState } from './TimerStateContext'
import { TimeWorkLogPanel, TimeWorkLogPanelHandle } from './TimeWorkLogPanel'
import { EncounterFormProps } from './types'
import { WeightBypassModal } from './WeightBypassModal'

const AUTOSAVE_INTERVAL_MS = 30_000

// Encounter types the autofill-from-notes backend supports (mirrors the switch in
// autofillEncounterFromNotes). The "Regenerate from notes" button only shows for these.
const AUTOFILL_SUPPORTED_TYPES = new Set<EncounterType>([
  EncounterType.ANTEPARTUM,
  EncounterType.PHYSICAL,
  EncounterType.ENCOUNTER,
  EncounterType.WELL_WOMAN,
  EncounterType.POSTPARTUM,
  EncounterType.BIRTHING_CENTER_ADMISSION,
])

const contactTypeLabels: Record<string, string> = {
  'phone-call': 'Phone Call',
  'video-call': 'Video Call',
  'in-person-without-visit': 'In-Person (No Visit)',
  'after-hours-contact': 'After-Hours Contact',
  'text-message': 'Text Message',
  'patient-portal-message': 'Patient Portal Message',
}

const ContactReviewBanner = ({
  contact,
  onAddToNote,
  addedToNote,
}: {
  contact: WithId<TelephoneContact>
  onAddToNote?: () => void
  addedToNote?: boolean
}) => {
  const { startFunctionsRef } = useTimerState()
  const [timerStarted, setTimerStarted] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  const typeLabel = contactTypeLabels[contact.contactType] ?? contact.contactType
  const dateStr = getDateString(contact.date, 'short', true)
  return (
    <Alert status="info" borderRadius={6} alignItems="flex-start" fontSize="sm" mb={2}>
      <AlertIcon mt={0.5} />
      <Box flex={1}>
        <AlertTitle fontSize="sm">
          Recent {typeLabel} — {dateStr}
        </AlertTitle>
        {contact.clinicalSummary && (
          <AlertDescription display="block" color="gray.600" noOfLines={2}>
            {contact.clinicalSummary}
          </AlertDescription>
        )}
      </Box>
      {/* M.6 Trigger 1: Add pre-visit contact section to this note */}
      {onAddToNote && (
        <Button
          size="xs"
          colorScheme={addedToNote ? 'green' : 'blue'}
          variant="outline"
          ml={2}
          flexShrink={0}
          isDisabled={addedToNote}
          onClick={onAddToNote}>
          {addedToNote ? 'Added ✓' : 'Add to this note'}
        </Button>
      )}
      <Button
        size="xs"
        colorScheme={timerStarted ? 'green' : 'blue'}
        variant="outline"
        ml={2}
        flexShrink={0}
        isDisabled={timerStarted}
        onClick={() => {
          startFunctionsRef.current['data.timeWorkLog.patientHistoryMinutes']?.()
          setTimerStarted(true)
        }}>
        {timerStarted ? 'Timer started ✓' : 'Start Review Timer'}
      </Button>
      <CloseButton ml={1} size="sm" onClick={() => setDismissed(true)} />
    </Alert>
  )
}

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)

const canEditPrevious = (type: EncounterSectionType) => {
  switch (type) {
    // case 'pregnancy-header':
    case 'allergies':
      return true
    default:
      return false
  }
}

const getSectionOpacity = (type: EncounterSectionType, canEdit: boolean) => {
  if (canEdit) return 1
  return type === 'notes' ? 1 : 0.65
}

const getSectionPointerEvents = (type: EncounterSectionType, canEdit: boolean) => {
  if (canEdit || type === 'notes') return 'auto'
  return 'none'
}

const SectionWrapper = ({
  section,
  children,
  index,
  isLast,
  isPreviousStep,
  editButton,
}: {
  section: EncounterSection
  children: (canEdit: boolean) => React.ReactNode
  index: number
  isLast: boolean
  isPreviousStep?: boolean
  editButton?: React.ReactNode
}) => {
  const canEdit = useMemo(
    () => !isPreviousStep || canEditPrevious(section.type),
    [section.type, isPreviousStep],
  )

  const body = (
    <Box
      w="100%"
      opacity={getSectionOpacity(section.type, canEdit)}
      pointerEvents={getSectionPointerEvents(section.type, canEdit)}>
      {children(canEdit)}
    </Box>
  )

  return (
    <>
      <Box
        bg={getRowBackground(index)}
        _dark={{ bg: getRowDarkBackground(index) }}
        w="100%"
        data-section-nav-index={index}>
        {body}
        {editButton && !canEdit ? (
          <Flex justify="flex-end" px={4} pb={3} pt={2}>
            {editButton}
          </Flex>
        ) : null}
      </Box>
      {!isLast ? <Divider borderColor="#ebebeb" /> : null}
    </>
  )
}

const FieldSection = ({ section }: { section: FieldEncounterSection }) => (
  <Box px={4} py={3} w="100%" data-child-name="data">
    <FormElement root showName={false} field={section.field} name="data" />
  </Box>
)

const renderSection = (
  section: EncounterSection,
  index: number,
  isLast: boolean,
  examType: EncounterType,
  canEdit: boolean,
  assessmentId: string | null,
) => {
  switch (section.type) {
    case 'field':
      return <FieldSection section={section} />
    case 'review-of-systems':
      return <ReviewOfSystemsInput key={`review-of-systems-${index}`} isLast={isLast} />
    case 'notes':
      return <EncounterNotesInput canEdit={canEdit} key={`notes-${index}`} />
    case 'vitals':
      return (
        <EncounterVitalsInput
          key={`vitals-${index}`}
          examType={examType}
          optional={section.optional}
        />
      )
    case 'risk-assessment':
      return <RiskAssessmentInput key={`risk-assessment-${index}`} />
    case 'clinical-rationale':
      return <ClinicalRationaleInput key={`clinical-rationale-${index}`} />
    // case 'pregnancy-header':
    //   return (
    //     <Box key={`pregnancy-header-${index}`} w="100%">
    //       <PregnancyHeader />
    //     </Box>
    //   )
    case 'reason-for-visit':
      return <ReasonForVisitRow key={`reason-for-visit-${index}`} />
    case 'allergies':
      return <ExamAllergiesInput key={`allergies-${index}`} />
    case 'medications-supplements':
      return <ExamMedicationsInput key={`medications-supplements-${index}`} />
    case 'labs-diagnostics':
      return <LabsInput key={`labs-diagnostics-${index}`} assessmentId={assessmentId} />
    case 'visit-location':
      return (
        <VisitLocationInput
          key={`visit-location-${index}`}
          isRoutineAntepartum={section.isRoutineAntepartum}
        />
      )
    case 'first-ob-visit':
      return <FirstObVisitInput key={`first-ob-visit-${index}`} />
    case 'soap-note':
      return <SoapNoteInput key={`soap-note-${index}`} />
    case 'general-illness-exam':
      return <GeneralIllnessExamInput key={`general-illness-exam-${index}`} />
    case 'remote-assessment':
      return <RemoteAssessmentInput key={`remote-assessment-${index}`} />
    case 'assessment-plan':
      return <AssessmentPlanInput key={`assessment-plan-${index}`} />
    case 'icd10-selection':
      return <ICD10SelectionInput key={`icd10-selection-${index}`} encounterType={examType} />
    case 'birthing-center-admission':
      return <BirthingCenterAdmissionInput key={`birthing-center-admission-${index}`} />
    case 'mdm':
      return <MdmInput key={`mdm-${index}`} />
    case 'physical-exam':
      return <PhysicalExamInput key={`physical-exam-${index}`} />
    case 'pelvic-exam':
      return <PelvicExamInput key={`pelvic-exam-${index}`} />
    case 'pre-visit-contact':
      return <PreVisitContactInput key={`pre-visit-contact-${index}`} />
    case 'prenatal-exam':
      return (
        <Box key={`prenatal-exam-${index}`} px={4} py={3} w="100%" data-child-name="data">
          <FormElement root showName={false} field={prenatalExamField} name="data" />
        </Box>
      )
    case 'ultrasound-header':
      return <UltrasoundHeaderInput key={`ultrasound-header-${index}`} />
    case 'ultrasound-findings':
      return (
        <UltrasoundFindingsInput key={`ultrasound-findings-${index}`} scenario={section.scenario} />
      )
    case 'dv-ipv-screening':
      return <DvIpvScreeningInput key={`dv-ipv-screening-${index}`} />
    case 'depression-screening':
      return <DepressionScreeningInput key={`depression-screening-${index}`} />
    case 'substance-use-counseling':
      return <SubstanceUseCounselingInput key={`substance-use-counseling-${index}`} />
    case 'patient-education':
      return (
        <PatientEducationChecklistInput
          key={`patient-education-${index}`}
          assessmentId={assessmentId}
          examType={examType}
        />
      )
  }
}

const NextStepButton = ({
  index,
  handleNext,
  isDisabled,
  isLoading,
}: {
  index: number
  handleNext: () => void
  isDisabled?: boolean
  isLoading?: boolean
}) => {
  const { errors, firstError } = useEncounterErrors()
  return (
    <Flex
      bg={getRowBackground(index)}
      _dark={{ bg: getRowDarkBackground(index) }}
      w="100%"
      align="center"
      justify="flex-end"
      gap={2}
      px={4}
      pb={3}>
      <Tooltip placement="left" hasArrow bg="orange.500" label={firstError?.text}>
        <SolidActionButton
          size="sm"
          onClick={handleNext}
          h={7}
          bg={greens.sage.hex}
          borderRadius="full"
          isDisabled={!!errors || isDisabled}
          isLoading={isLoading}>
          Next Step
        </SolidActionButton>
      </Tooltip>
    </Flex>
  )
}

type SectionWithIndex<T extends EncounterSectionType> = TypedEncounterSection<T> & {
  index: number
}

type SectionsWithIndices<T extends EncounterSectionType = EncounterSectionType> =
  SectionWithIndex<T>[]

const addIndicesToSections = (sections: EncounterSections[]): SectionsWithIndices[] =>
  sections.reduce(
    (acc, sections) => ({
      currIndex: acc.currIndex + sections.length,
      sections: [
        ...acc.sections,
        sections.map((section, sectionIndex) => ({
          ...section,
          index: sectionIndex + acc.currIndex,
        })),
      ],
    }),
    { currIndex: 0, sections: [] as SectionsWithIndices[] },
  ).sections

// Generate the wizard steps to render on (re)init. `targetStep` is the persisted `currentStep` from
// the encounter — render steps 0..targetStep. When `targetStep` is null (legacy encounters saved
// before step tracking existed), fall back to data-completeness inference: reveal left-to-right and
// stop at the first section whose applicable fields aren't all defined. For a new note pass
// targetStep = 0 (formData = {}) → just step 0.
const buildInitialSteps = (
  generators: SectionGenerator[],
  assessmentId: string | null,
  encountersArr: Encounter[],
  formData: FieldMapValue,
  targetStep: number | null,
): { steps: EncounterSections[]; activeIndex: number } => {
  const steps: EncounterSections[] = []
  let previousSections: EncounterSections = []
  let activeIndex = 0
  for (let i = 0; i < generators.length; i++) {
    const sections = generators[i](assessmentId, encountersArr, formData, previousSections)
    steps.push(sections)
    previousSections = [...previousSections, ...sections]
    activeIndex = i
    if (targetStep === null) {
      // Legacy fallback: stop at the first incomplete step.
      if (!sections.every(section => isEncounterSectionComplete(section, formData))) break
    } else if (i >= targetStep) {
      // Explicit tracking: render exactly up to the persisted step.
      break
    }
  }
  return { steps, activeIndex }
}

export const EncounterFormBody = <T extends EncounterType>({
  loading,
  examId,
  patientId,
  assessmentId,
  type,
  examConfig,
  onClose,
  onNewValidate,
  validate,
  initialEncounterData,
  initialCurrentStep,
  currentStepRef,
  amendmentReason,
  onAmendmentReasonConsumed,
}: {
  loading: boolean
  examId: string | null
  patientId: string
  assessmentId: string | null
  type: T
  examConfig: EncounterTypeConfig<T> | null
  onClose: () => void
  onNewValidate: (validate: FormValidate<AnyObject> | undefined) => void
  validate: FormValidate<AnyObject> | undefined
  initialEncounterData: FieldMapValue | null
  initialCurrentStep?: number
  currentStepRef: RefObject<number>
  amendmentReason?: string
  onAmendmentReasonConsumed?: () => void
}) => {
  const { encounters } = useProfile()
  const { dataArr: encountersArr } = encounters
  const typeName = useMemo(() => encounterTypeName[type], [type])
  const { width } = useFormWidth()
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [autofilling, setAutofilling] = useState(false)
  const [weightBypassOpen, setWeightBypassOpen] = useState(false)
  // Clinical-rationale suggestions surfaced in the clinical-rationale section: bundled from the last
  // autofill run, or fetched on demand. null = nothing fetched yet (the section falls back to its own fetch).
  const [clinicalRationaleSuggestions, setClinicalRationaleSuggestions] = useState<
    ClinicalRationaleMatch[] | null
  >(null)
  const [rationaleLoading, setRationaleLoading] = useState(false)
  const timeWorkLogRef = useRef<TimeWorkLogPanelHandle>(null)
  const headerRef = useRef<EncounterFormHeaderHandle>(null)
  const form = useForm()
  const toast = useToast()
  const { includePreVisitContact, setIncludePreVisitContact } = useEncounterForm()

  // §2.17: 24-hour same-patient prompt — only for antepartum encounters.
  // Pass null for other encounter types to skip the Firestore query.
  const isAntepartum = type === EncounterType.ANTEPARTUM
  const { dataArr: recentContacts } = useAssessmentContacts(
    patientId,
    isAntepartum ? assessmentId : null,
  )
  const recentContact = useMemo(() => {
    if (!recentContacts) return null
    const cutoff = Date.now() - 24 * 60 * 60 * 1000
    return (
      recentContacts
        .filter(c => new Date(c.date).getTime() >= cutoff)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0] ?? null
    )
  }, [recentContacts])

  // §2.11: On mount, if the form was opened via the Amend flow, write the amendment
  // reason to data.amendmentReason so it is persisted when the provider re-authenticates.
  useEffect(() => {
    if (amendmentReason) {
      form.change('data.amendmentReason', amendmentReason)
      onAmendmentReasonConsumed?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // §2.15.4: On mount, notify provider if this note was pre-filled from a Quick Note.
  useEffect(() => {
    const values = form.getState().values
    if (values?.data?.quickNoteOrigin) {
      toast({
        title: 'Pre-filled from Quick Note',
        description:
          'This note was pre-filled from a Quick Note. Please review all fields before authenticating.',
        status: 'info',
        duration: 8000,
        isClosable: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-save: fire every AUTOSAVE_INTERVAL_MS when the form has unsaved changes.
  // Uses form.submit() directly — bypasses the Time & Work Log panel (authenticate path)
  // and the weight bypass modal (both are gated on handleTimeLogConfirm, not on submit).
  useEffect(() => {
    const id = setInterval(async () => {
      if (autoSavingRef.current) return
      if (!form.getState().dirty) return
      autoSavingRef.current = true
      try {
        await form.submit()
      } finally {
        autoSavingRef.current = false
      }
    }, AUTOSAVE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [form])

  // §2.4: Weight bypass modal fires for routine antepartum only (Tier 1.5 — non-routine is Tier 2).
  const isRoutineAntepartum =
    type === EncounterType.ANTEPARTUM && (examConfig as any)?.visitType !== 'non-routine'

  const isNonRoutineAntepartum =
    type === EncounterType.ANTEPARTUM && (examConfig as any)?.visitType === 'non-routine'

  // §M.3: On mount, focus the first active field so the provider can start typing immediately.
  // Only fires for new notes (examId === null). Uses a ref guard to prevent re-firing on re-renders.
  const initialFocusFiredRef = useRef(false)
  useEffect(() => {
    if (examId !== null) return
    if (initialFocusFiredRef.current) return
    initialFocusFiredRef.current = true
    const timer = setTimeout(() => {
      if (isRoutineAntepartum) {
        form.focus('data.reasonForVisit')
      } else if (isNonRoutineAntepartum) {
        form.focus('data.soapNote.subjective')
      }
    }, 150)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOpenTimeLogForAuth = useCallback(() => {
    timeWorkLogRef.current?.open('Authenticate')
  }, [])
  const handleTimeLogConfirm = useCallback(() => {
    const { values } = form.getState()
    const weight = values?.data?.vital?.weight
    const modality = values?.data?.visitLocation?.modality
    const isTelehealth = modality === 'telehealth-video' || modality === 'telehealth-audio-only'
    // §2.1 Telehealth: weight drops to Tier 2 — bypass modal only fires for in-person visits.
    if (isRoutineAntepartum && !weight && !isTelehealth) {
      setWeightBypassOpen(true)
    } else {
      headerRef.current?.openAuthModal()
    }
  }, [form, isRoutineAntepartum])

  const handleWeightBypassProceed = useCallback(() => {
    form.change('data.vital.weightBypassAcknowledgedAt', new Date().toISOString())
    setWeightBypassOpen(false)
    headerRef.current?.openAuthModal()
  }, [form])

  const handleWeightBypassCancel = useCallback(() => {
    setWeightBypassOpen(false)
  }, [])

  // §M.6: Provider opts in to the pre-visit contact section rather than it appearing by default.
  // Triggers: ContactReviewBanner "Add to this note" button, or the explicit link below.
  // const [includePreVisitContact, setIncludePreVisitContact] = useState(false)

  const generators = useMemo(() => encounterFields[type](examConfig), [type, examConfig])

  // Render steps from the persisted step number so an auto-save remount (examId null → id) lands the
  // provider exactly where they left off rather than jumping to the last generated step. New note →
  // step 0; existing encounter → its stored currentStep; existing without one (legacy) → null, which
  // makes buildInitialSteps fall back to data-completeness inference. Computed once per mount.
  const initialTargetStep = examId ? (initialCurrentStep ?? null) : 0
  const initialStepFormData = examId ? (initialEncounterData ?? {}) : {}
  const [sectionsByStep, setSectionsByStep] = useState<SectionsWithIndices[]>(() =>
    addIndicesToSections(
      buildInitialSteps(
        generators,
        assessmentId,
        encountersArr ?? [],
        initialStepFormData,
        initialTargetStep,
      ).steps,
    ),
  )
  const [activeGeneratorIndex, setActiveGeneratorIndex] = useState(
    () =>
      buildInitialSteps(
        generators,
        assessmentId,
        encountersArr ?? [],
        initialStepFormData,
        initialTargetStep,
      ).activeIndex,
  )

  // Keep currentStepRef in sync with the active step (covers init, handleNext, and handleEdit) so the
  // parent's save payload always carries the up-to-date step. Auto-save never advances it because only
  // explicit step changes move activeGeneratorIndex.
  useEffect(() => {
    currentStepRef.current = activeGeneratorIndex
  }, [activeGeneratorIndex, currentStepRef])

  // §M.6: Prepend { type: 'pre-visit-contact' } to step 0 when the provider opts in.
  // We re-index all sections in step 0 so overall indices stay contiguous.
  const effectiveSectionsByStep = useMemo<SectionsWithIndices[]>(() => {
    if (!includePreVisitContact || !isAntepartum) return sectionsByStep
    const [firstStep, ...rest] = sectionsByStep
    if (!firstStep) return sectionsByStep
    const injected: SectionWithIndex<'pre-visit-contact'> = {
      type: 'pre-visit-contact',
      index: 0,
    }
    const shiftedFirst = firstStep.map(s => ({ ...s, index: s.index + 1 }))
    return [
      [injected, ...shiftedFirst] as SectionsWithIndices,
      ...rest.map(step => step.map(s => ({ ...s, index: s.index + 1 }))),
    ]
  }, [sectionsByStep, includePreVisitContact, isAntepartum])

  const autoSavingRef = useRef(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const currentStepScrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeGeneratorIndex === 0 || !currentStepScrollRef.current || !scrollContainerRef.current)
      return
    const container = scrollContainerRef.current
    // container.scrollTop = currentStepScrollRef.current.offsetTop - container.offsetTop
    container.scrollTo({
      top: currentStepScrollRef.current.offsetTop - container.offsetTop,
      behavior: 'smooth',
    })
  }, [activeGeneratorIndex])

  const isLastStep = activeGeneratorIndex >= generators.length - 1

  const allRenderedSections = useMemo(
    () => effectiveSectionsByStep.slice(0, activeGeneratorIndex + 1).flat(),
    [effectiveSectionsByStep, activeGeneratorIndex],
  )

  useEncounterValidate(onNewValidate, allRenderedSections, encountersArr)

  const currentStepHasNotes = useMemo(
    () => (effectiveSectionsByStep[activeGeneratorIndex] ?? []).some(s => s.type === 'notes'),
    [effectiveSectionsByStep, activeGeneratorIndex],
  )

  // Run the autofill-from-notes request and apply it to the form. `overwrite=false` (Next Step) only
  // fills empty fields; `overwrite=true` (Regenerate button) replaces them. Clinical-rationale
  // suggestions returned alongside the data are stored for the clinical-rationale section to display.
  const runAutofill = useCallback(
    async (notesText: string, { overwrite }: { overwrite: boolean }) => {
      if (!notesText?.trim()) return
      setAutofilling(true)
      try {
        const { data: result } = await autofillEncounterFromNotes({
          notes: notesText,
          encounterType: type,
          visitType: (examConfig as any)?.visitType ?? null,
        })
        const formData = (form.getState().values?.data ?? {}) as FieldMapValue
        form.batch(() => {
          const d = result.data
          const autofill = (key: keyof typeof d) => {
            const newVal = d[key]
            if (!isEmpty(newVal) && (overwrite || isEmpty(formData[key]))) {
              form.change(`data.${key}`, newVal)
            }
          }
          // vitals
          autofill('vital')
          // prenatal interval history
          autofill('fundalHeightLandmark')
          autofill('fundalHeightMeasurement')
          autofill('fundalHeightUnits')
          autofill('fetalPosition')
          autofill('fetalHeartTones')
          autofill('edema')
          autofill('edemaLocation')
          autofill('fetalMovement')
          autofill('kickCount')
          autofill('contractions')
          autofill('contractionFrequency')
          autofill('contractionDuration')
          autofill('vaginalBleeding')
          autofill('vaginalBleedingDetails')
          autofill('leakingFluid')
          autofill('leakingFluidDetails')
          autofill('headache')
          autofill('visualChanges')
          autofill('epigastricPain')
          autofill('epigastricPainDetails')
          autofill('urinarySymptoms')
          autofill('nauseaVomiting')
          autofill('nutritionHydration')
          autofill('physicalActivity')
          autofill('bowelGISymptoms')
          autofill('moodScale')
          autofill('moodNotes')
          autofill('patientConcerns')
          // physical exam
          autofill('hasPhysicalExam')
          autofill('physical')
          autofill('general')
          autofill('hasPelvicExam')
          autofill('pelvic')
          autofill('hasUrineExam')
          autofill('urine')
          autofill('chiefComplaint')
          autofill('hasReviewOfSystems')
          autofill('externalRecordsReviewed')
          autofill('externalRecordsReviewedText')
          // non-routine antepartum (clinicalRelationshipToPregnancy is intentionally NOT auto-filled —
          // it is surfaced as clinicalRationaleSuggestions below)
          autofill('generalIllnessExam')
          autofill('soapNote')
          // assessment & plan
          autofill('assessment')
          autofill('planItems')
          autofill('referrals')
          autofill('patientEducation')
          autofill('antepartumRisk')
          autofill('antepartumRiskReason')
          autofill('icd10Codes')
          autofill('returnVisitInterval')
          // physical / encounter / well-woman
          autofill('riskAssessment')
          // postpartum
          autofill('lochia')
          autofill('fundus')
          autofill('perineum')
          // birthing center admission
          autofill('admissionType')
          autofill('membraneStatus')
          autofill('membraneFluidColor')
          autofill('membraneFluidAmount')
          autofill('supervisingProvider')
          autofill('lastOralIntakeContent')
          autofill('interpreterNeeded')
          autofill('interpreterLanguage')
          autofill('birthPlanOnFile')
        })
        setClinicalRationaleSuggestions(result.clinicalRationaleSuggestions ?? null)
      } catch (error) {
        console.error('AI autofill failed, continuing without prefill:', error)
      } finally {
        setAutofilling(false)
      }
    },
    [type, examConfig, form],
  )

  const regenerateAutofill = useCallback(() => {
    const notes = (form.getState().values?.data?.notes as string | undefined) ?? ''
    void runAutofill(notes, { overwrite: true })
  }, [form, runAutofill])

  const fetchRationaleSuggestions = useCallback((chiefComplaint: string) => {
    if (!chiefComplaint?.trim()) return
    setRationaleLoading(true)
    getPregnancyClinicalRationale({ chiefComplaint })
      .then(({ data }) => setClinicalRationaleSuggestions(data.matches))
      .catch(error => console.error('Clinical rationale fetch failed:', error))
      .finally(() => setRationaleLoading(false))
  }, [])

  const handleNext = useCallback(async () => {
    const nextIdx = activeGeneratorIndex + 1
    if (nextIdx >= generators.length) return
    const { values } = form.getState()
    const formData = (values?.data ?? {}) as FieldMapValue
    const notesText = formData.notes as string | undefined

    if (currentStepHasNotes && notesText?.trim()) {
      await runAutofill(notesText, { overwrite: false })
    }

    const previousSections = sectionsByStep.slice(0, activeGeneratorIndex + 1).flat()
    const nextSections = generators[nextIdx](
      assessmentId,
      encountersArr ?? [],
      formData,
      previousSections,
    )
    setSectionsByStep(prev => addIndicesToSections([...prev.slice(0, nextIdx), nextSections]))
    setActiveGeneratorIndex(nextIdx)
    // Mark the form dirty so auto-save fires and persists the new step (the value is read from
    // currentStepRef, kept in sync with activeGeneratorIndex). Without this a Next-then-idle could
    // leave the advance unsaved if the form happened to be pristine.
    form.change('currentStep', nextIdx)
  }, [
    currentStepHasNotes,
    runAutofill,
    activeGeneratorIndex,
    generators,
    form,
    sectionsByStep,
    encountersArr,
    assessmentId,
  ])

  const handleEdit = useCallback(
    (stepIndex: number) => {
      setActiveGeneratorIndex(stepIndex)
      setSectionsByStep(prev => prev.slice(0, stepIndex + 1))
      form.change('currentStep', stepIndex)
    },
    [form],
  )

  // The Notes "Regenerate from notes" button shows only once the provider has advanced past the notes
  // step, on an autofill-supported encounter type.
  const notesStepIndex = useMemo(
    () => effectiveSectionsByStep.findIndex(step => step.some(s => s.type === 'notes')),
    [effectiveSectionsByStep],
  )
  const canRegenerateAutofill =
    AUTOFILL_SUPPORTED_TYPES.has(type) &&
    notesStepIndex >= 0 &&
    activeGeneratorIndex > notesStepIndex

  const autofillContextValue = useMemo<AutofillContextValue>(
    () => ({
      autofilling,
      canRegenerateAutofill,
      regenerateAutofill,
      clinicalRationaleSuggestions,
      rationaleLoading,
      fetchRationaleSuggestions,
    }),
    [
      autofilling,
      canRegenerateAutofill,
      regenerateAutofill,
      clinicalRationaleSuggestions,
      rationaleLoading,
      fetchRationaleSuggestions,
    ],
  )

  if (!width || loading)
    return (
      <Center w="100%" h="100%">
        <Spinner />
      </Center>
    )

  const prevStepSections = effectiveSectionsByStep.slice(0, activeGeneratorIndex)
  const currentStepSections = effectiveSectionsByStep[activeGeneratorIndex] ?? []

  return (
    <TimerFlushProvider>
      <TimerStateProvider>
        <EncounterErrorsProvider sections={allRenderedSections} validate={validate}>
          <Flex w="100%" h="100%" justify="center" align="center" overflow="hidden">
            <Flex
              flexFlow="column"
              w="100%"
              maxW="1200px"
              h="100%"
              bg="gray.100"
              align="center"
              // borderRadius={4}
              // boxShadow="1px 1px 4px #00000077"
            >
              <EncounterFormHeader
                ref={headerRef}
                examId={examId}
                patientId={patientId}
                assessmentId={assessmentId}
                typeName={typeName}
                examConfig={examConfig}
                examType={type}
                onClose={onClose}
                confirmingCancel={confirmingCancel}
                setConfirmingCancel={setConfirmingCancel}
                allSectionsRendered={isLastStep}
                onRequestAuthenticate={handleOpenTimeLogForAuth}
              />
              <Flex w="100%" position="relative" bg="gray.50" flex={1} minH={0}>
                <EncounterSectionNav
                  sections={allRenderedSections}
                  scrollContainerRef={scrollContainerRef}
                />
                <Box
                  ref={scrollContainerRef}
                  h="100%"
                  overflowY="auto"
                  flex={1}
                  minW="0"
                  opacity={confirmingCancel ? 0.5 : 1}
                  bg="white"
                  w="100%">
                  <AutofillProvider value={autofillContextValue}>
                    <VStack w="100%" alignItems="flex-start" spacing={0}>
                      {/* §M.6 / V2: documentation mode toggle — see §1.3 */}
                      {recentContact && isAntepartum && (
                        <Box w="100%" px={3} pt={3}>
                          <ContactReviewBanner
                            contact={recentContact}
                            onAddToNote={() => setIncludePreVisitContact(true)}
                            addedToNote={includePreVisitContact}
                          />
                        </Box>
                      )}
                      {/* §M.6 Trigger 2: explicit opt-in link — shown only for antepartum when not yet added */}

                      {prevStepSections.map((stepSections, stepIdx) => (
                        <Box key={stepIdx} w="100%">
                          {stepSections.map((section, i) => (
                            <SectionWrapper
                              index={section.index}
                              key={i}
                              section={section}
                              isPreviousStep={!examId}
                              isLast={i === stepSections.length - 1}
                              editButton={
                                !examId ? (
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    bg="white"
                                    _dark={{ bg: 'gray.900' }}
                                    onClick={() => handleEdit(stepIdx)}
                                    leftIcon={<EditIcon width={3} height={3} />}>
                                    Edit {getSectionName(section)}
                                  </Button>
                                ) : undefined
                              }>
                              {canEdit =>
                                renderSection(
                                  section,
                                  i,
                                  i === stepSections.length - 1,
                                  type,
                                  canEdit,
                                  assessmentId,
                                )
                              }
                            </SectionWrapper>
                          ))}
                          <Divider borderColor="#ebebeb" />
                        </Box>
                      ))}
                      <div ref={currentStepScrollRef} />
                      {currentStepSections.map((section, i) => (
                        <SectionWrapper
                          key={i}
                          index={section.index}
                          section={section}
                          isLast={i === currentStepSections.length - 1}>
                          {canEdit =>
                            renderSection(
                              section,
                              i,
                              i === currentStepSections.length - 1,
                              type,
                              canEdit,
                              assessmentId,
                            )
                          }
                        </SectionWrapper>
                      ))}
                      {!isLastStep && (
                        <NextStepButton
                          index={currentStepSections[currentStepSections.length - 1].index}
                          handleNext={handleNext}
                          isDisabled={autofilling}
                          isLoading={autofilling}
                        />
                      )}
                    </VStack>
                  </AutofillProvider>
                </Box>
                <TimeWorkLogPanel ref={timeWorkLogRef} onConfirm={handleTimeLogConfirm} />
              </Flex>
              <EncounterFormFooter encounterType={type} />
            </Flex>
          </Flex>
        </EncounterErrorsProvider>
      </TimerStateProvider>
      <WeightBypassModal
        isOpen={weightBypassOpen}
        onProceed={handleWeightBypassProceed}
        onCancel={handleWeightBypassCancel}
      />
    </TimerFlushProvider>
  )
}

const innerFormBoxProps: BoxProps = {
  h: '100%',
}

const EncounterFormContent = <T extends EncounterType>({
  patientId,
  openEncounter,
  assessmentId,
  onClose,
  onSuccess,
  amendmentReason,
  onAmendmentReasonConsumed,
}: EncounterFormProps<T>) => {
  const [, setUploads] = useState<Record<string, UploadProgress> | null>(null)
  // Furthest step the provider has advanced to — kept in sync by EncounterFormBody and sent on every
  // save so reopen renders only steps 0..currentStep.
  const currentStepRef = useRef(0)

  const { appName } = useApp()
  const { authUser } = useAuth()
  const { data: userData } = useCachedUser(authUser?.uid ?? null)
  const { practiceId: gynPracticeId } = useGynCareView()
  const {
    selectedAssessment: { populated: assessment },
  } = useProfile()

  const { encounters } = useProfile()
  const { dataArr: encountersArr } = encounters
  const { type, examId, config: encounterConfig } = openEncounter
  const practiceId = useMemo(() => {
    switch (type) {
      case EncounterType.ANTEPARTUM:
      case EncounterType.POSTPARTUM:
      case EncounterType.BIRTHING_CENTER_ADMISSION:
        return assessment?.midwifeId ?? null
      default:
        return gynPracticeId
    }
  }, [type, assessment, gynPracticeId])

  const { data: encounter, loading: encounterLoading } = useDocument<Encounter>(ENCOUNTERS, examId)

  // Per-user input size preference — live-subscribed, so a change from the header's settings
  // popover resizes the form immediately. Defaults to 'sm' (the previous hardcoded size).
  const { data: chartingSettings } = useChartingFormSettings()
  const inputSize = chartingSettings?.encounterForms?.inputSize ?? DEFAULT_ENCOUNTER_INPUT_SIZE

  // Clear savedExam when examId changes (navigating to a different exam)
  const [validate, setValidate] = useState<((data: AnyObject) => ValidationErrors) | undefined>(
    () => undefined,
  )

  // Wrap setValidate so the function is always stored as a value, not called as a
  // React state updater (React calls any function arg as `fn(prevState)` instead of storing it).
  const handleNewValidate = useCallback((fn: FormValidate<AnyObject> | undefined) => {
    setValidate(() => fn)
  }, [])

  const validationField = useMemo(
    () =>
      getEncounterValidationField(assessmentId ?? null, encountersArr ?? [], type, encounterConfig),
    [type, encounterConfig, encountersArr, assessmentId],
  )
  const optionalField = useMemo(() => makeAllFieldsOptional(validationField), [validationField])

  const warnNavigationIfDirty = useMemo(() => ({ formName: encounterTypeName[type] }), [type])
  // Use savedExam (from server response) if available, fall back to Firestore data

  // Resolve the encounter config (e.g. antepartum visitType) from the open selection, falling back to
  // the loaded doc. Used both for the save payload and to drive the form body.
  const config = useMemo(
    () => encounterConfig ?? (encounter?.config as EncounterTypeConfig<T> | undefined) ?? null,
    [encounterConfig, encounter],
  )

  const handleSubmit = useCallback(
    async (
      data: FieldMapValue,
      _onUploadProgress: (progress: Record<string, UploadProgress>) => void,
      _form?: FormApi,
    ) => {
      if (!patientId) return { [FORM_ERROR]: 'Patient ID is required' }
      if (!practiceId) return { [FORM_ERROR]: 'Practice ID is required' }
      try {
        // §A: Build noteAuthor for new encounters only — captured at first save, immutable after.
        const noteAuthor =
          examId || !authUser
            ? undefined
            : {
                userId: authUser.uid,
                displayName: getFullName(userData) || authUser.uid,
                initials: userData ? getExpectedInitials(userData) : '',
                capturedAt: Date.now(),
              }
        // §A: Extract performingProvider from root-level form state (not inside data.something).
        const performingProvider = (data.performingProvider as string | null | undefined) ?? null
        const { data: res } = await saveEncounter({
          appName,
          encounterId: examId,
          type,
          patientId,
          practiceId,
          assessmentId: assessmentId ?? null,
          noteAuthor,
          performingProvider,
          ...data,
          // After ...data so the resolved values win over anything in form state. config carries the
          // antepartum visitType (routine vs non-routine) — it isn't a form field, so it must be sent
          // explicitly or it's lost on a new encounter's first save.
          config,
          currentStep: currentStepRef.current,
        } as unknown as SaveEncounterArgs)
        // Store server-returned exam in state so it flows into initialValues
        // directly, rather than calling form.restart which gets overridden
        // by the <Form initialValues> prop on the next render
        onSuccess({ type, examId: res.id, config })
      } catch (error: any) {
        console.error(error)
        return { [FORM_ERROR]: error?.message || 'An error occurred while saving the exam' }
      }
      return undefined
    },
    [
      appName,
      examId,
      type,
      patientId,
      practiceId,
      assessmentId,
      onSuccess,
      config,
      authUser,
      userData,
    ],
  )
  const initialData = useMemo(() => {
    if (!encounter) return {}
    if (encounter.draftData) {
      return { ...encounter, data: { ...(encounter.data ?? {}), ...encounter.draftData } }
    }
    return encounter
  }, [encounter])

  if (!encounter && (encounterLoading || examId))
    return (
      <Center w="100%" h="100%">
        <Spinner />
      </Center>
    )

  return (
    <InnerForm
      field={optionalField}
      data={initialData}
      setUploads={setUploads}
      warnNavigationIfDirty={warnNavigationIfDirty}
      onSubmit={handleSubmit}
      boxProps={innerFormBoxProps}>
      <DotPhraseProvider practiceId={practiceId}>
        <ThemeProvider inputSize={inputSize} theme="detailed">
          <EncounterFormBody
            loading={encounterLoading}
            onNewValidate={handleNewValidate}
            validate={validate}
            examId={examId}
            patientId={patientId}
            assessmentId={assessmentId ?? null}
            type={type}
            examConfig={config}
            onClose={onClose}
            initialEncounterData={
              examId ? (((initialData as Encounter)?.data ?? null) as FieldMapValue) : null
            }
            initialCurrentStep={examId ? (initialData as Encounter)?.currentStep : undefined}
            currentStepRef={currentStepRef}
            amendmentReason={amendmentReason}
            onAmendmentReasonConsumed={onAmendmentReasonConsumed}
          />
        </ThemeProvider>
      </DotPhraseProvider>
    </InnerForm>
  )
}

export const EncounterForm = <Type extends EncounterType>(props: EncounterFormProps<Type>) => (
  <EncounterFormProvider {...props}>
    <EncounterFormContent {...props} />
  </EncounterFormProvider>
)
